// Column model shared by the Staff Excel template generator and the upload
// validator. Order here IS the column order in the generated sheet.

export type ColKind =
  | 'text'
  | 'number'
  | 'date'
  | 'title'
  | 'schedule'
  | 'location'
  | 'gender'
  | 'status'
  | 'yesno'
  | 'basic'
  | 'gradeHelper'
  | 'minHelper'
  | 'maxHelper'

export interface StaffCol {
  key: string
  header: string
  required?: boolean
  kind: ColKind
  width?: number
}

export const STAFF_COLUMNS: StaffCol[] = [
  { key: 'personnel_number', header: 'Personnel # (optional)', kind: 'number', width: 16 },
  { key: 'first_name', header: 'First name', required: true, kind: 'text', width: 16 },
  { key: 'middle_name', header: 'Middle name', kind: 'text', width: 16 },
  { key: 'last_name', header: 'Last name', required: true, kind: 'text', width: 16 },
  { key: 'gender', header: 'Gender', kind: 'gender', width: 12 },
  { key: 'date_of_birth', header: 'Date of birth', kind: 'date', width: 14 },
  { key: 'phone', header: 'Phone', kind: 'text', width: 16 },
  { key: 'email', header: 'Email', kind: 'text', width: 22 },
  { key: 'date_of_employment', header: 'Date of employment', required: true, kind: 'date', width: 18 },
  { key: 'employment_status', header: 'Employment status', kind: 'status', width: 16 },
  { key: 'department', header: 'Department', kind: 'text', width: 16 },
  { key: 'location_name', header: 'Location', required: true, kind: 'location', width: 18 },
  { key: 'job_title', header: 'Job title', required: true, kind: 'title', width: 22 },
  { key: 'schedule_name', header: 'Working schedule', required: true, kind: 'schedule', width: 20 },
  { key: 'grade', header: 'Grade (auto)', kind: 'gradeHelper', width: 18 },
  { key: 'basic_min', header: 'Band min (auto)', kind: 'minHelper', width: 15 },
  { key: 'basic_max', header: 'Band max (auto)', kind: 'maxHelper', width: 15 },
  { key: 'basic_salary', header: 'Basic salary (annual)', required: true, kind: 'basic', width: 18 },
  { key: 'nhf_enrolled', header: 'NHF enrolled', kind: 'yesno', width: 13 },
  { key: 'residential_address', header: 'Residential address', kind: 'text', width: 24 },
  { key: 'residential_city', header: 'City', kind: 'text', width: 14 },
  { key: 'residential_state', header: 'State', kind: 'text', width: 14 },
  { key: 'residential_country', header: 'Country', kind: 'text', width: 14 },
  { key: 'bank_name', header: 'Bank name', kind: 'text', width: 18 },
  { key: 'bank_account_name', header: 'Bank account name', kind: 'text', width: 20 },
  { key: 'bank_account_number', header: 'Bank account number', kind: 'text', width: 20 },
  { key: 'national_id', header: 'NIN', kind: 'text', width: 14 },
  { key: 'tin', header: 'TIN', kind: 'text', width: 14 },
  { key: 'pension_pin', header: 'Pension PIN', kind: 'text', width: 16 },
  { key: 'pension_fund_administrator', header: 'Pension fund administrator', kind: 'text', width: 24 },
  { key: 'emergency_contact_name', header: 'Emergency contact name', kind: 'text', width: 22 },
  { key: 'emergency_contact_phone', header: 'Emergency contact phone', kind: 'text', width: 22 },
  { key: 'emergency_contact_relationship', header: 'Emergency contact relationship', kind: 'text', width: 26 },
]

/** 1-based Excel column letter for a 1-based column index. */
export function colLetter(index1: number): string {
  let n = index1
  let s = ''
  while (n > 0) {
    const rem = (n - 1) % 26
    s = String.fromCharCode(65 + rem) + s
    n = Math.floor((n - 1) / 26)
  }
  return s
}

/** kind → 1-based column index (first match). */
export function colIndexOf(kind: ColKind): number {
  const i = STAFF_COLUMNS.findIndex(c => c.kind === kind)
  return i + 1
}

export interface StaffTemplateGrade {
  id: string
  name: string
  grade_type: 'fixed' | 'range'
  min: number | null
  max: number | null
  fixed: number | null
}

export interface StaffTemplateTitle {
  id: string
  title_name: string
  job_title_id: string | null
  grade_id: string
  grade_name: string
}

export interface StaffTemplateContext {
  tenantId: string
  grades: StaffTemplateGrade[]
  titles: StaffTemplateTitle[]
  schedules: { id: string; name: string }[]
  locations: { id: string; name: string }[]
  existingPersonnel: number[]
  personnelNumberNext: number
}
